import React, { useCallback, useContext, useEffect, useState } from 'react';
import { isEmpty } from 'lodash';
import { useParams } from 'react-router-dom';
import {
  DOCUMENT_MULTIPLE_FILES_UPLOAD_ERROR,
  DOCUMENT_MULTIPLE_FILES_UPLOAD_SUCCESS,
} from 'common/constants/messages/validations';
import { RequestedFileExtended, RequestedFileFulfillmentStatus } from 'common/types/files';
import { TaskContext } from 'context/TaskContext';
import { TaskContextType } from 'context/TaskContext/types';
import FilesService from 'services/documents';
import { useResponse } from 'services/hooks';
import useHandleFiles from 'services/hooks/useHandleFiles';
import { Logger } from 'utilities';
import { StyledPerfectScrollbar } from './ProcessManagementDocumentList.styles';
import { ProcessManagementDocumentListProps } from './types';
import EmptyFileBox from '../EmptyFileBox/EmptyFileBox';
import FileAccordion from '../FileAccordion/FileAccordion';

const filesService = new FilesService();

const ProcessManagementDocumentList = ({
  documentsToShow,
  setUploadedFiles,
  companyDocuments,
}: ProcessManagementDocumentListProps) => {
  const { task, validateToken } = useContext(TaskContext) as TaskContextType;
  const { requestId, hmacToken } = useParams<{ requestId?: string; hmacToken?: string }>();
  const [loadingFiles, setLoadingFiles] = useState<{ [key: string]: boolean }>({});
  const [sortedDocuments, setSortedDocuments] = useState<RequestedFileExtended[]>([]);
  const [isMarkingFulfillment, setIsMarkingFulfillment] = useState(false);
  const { errorNotification, successNotification } = useResponse();
  const { progress, validateFiles, uploadMultipleFiles } = useHandleFiles({});

  const handleUploadFiles = useCallback(
    async (event: React.ChangeEvent<HTMLInputElement>, requestedFileId: RequestedFileExtended['id']) => {
      const { files } = event.target as HTMLInputElement;
      const areMultipleFiles = files && files.length > 1;

      if (!files) {
        return;
      }

      // Validate the uploaded files
      if (!validateFiles(files)) {
        return;
      }

      const fileIdStr = String(requestedFileId);

      try {
        // Set loading state to true for all files
        setLoadingFiles(prevState => ({ ...prevState, [fileIdStr]: true }));

        await uploadMultipleFiles({
          files,
          task,
          requestedFileId,
          setUploadedFiles,
          companyDocuments,
          showNotifications: !areMultipleFiles,
        });

        // Validate the token after all uploads are completed
        validateToken();
      } catch (error) {
        // Notify the user if an error occurs during upload
        errorNotification(DOCUMENT_MULTIPLE_FILES_UPLOAD_ERROR);
        Logger.error(DOCUMENT_MULTIPLE_FILES_UPLOAD_ERROR, error);
      } finally {
        if (areMultipleFiles) {
          successNotification(DOCUMENT_MULTIPLE_FILES_UPLOAD_SUCCESS);
        }
        // Set loading state to false after all uploads are complete
        setLoadingFiles(prevState => ({ ...prevState, [fileIdStr]: false }));
      }
    },
    [
      task,
      setUploadedFiles,
      companyDocuments,
      validateToken,
      errorNotification,
      successNotification,
      uploadMultipleFiles,
      validateFiles,
    ]
  );

  const handleMarkFulfillment = useCallback(
    async (
      requestedFileId: number,
      status: RequestedFileFulfillmentStatus,
      note: string | null
    ) => {
      if (!requestId || !hmacToken) {
        return;
      }
      try {
        setIsMarkingFulfillment(true);
        await filesService.markRequestedFileFulfillmentPortal(requestId, hmacToken, requestedFileId, {
          fulfillment_status: status,
          fulfillment_note: note,
        });
        successNotification('Document marked successfully.');
        validateToken();
      } catch (error) {
        errorNotification('Failed to update document status.');
        Logger.error('Failed to mark requested file fulfillment:', error);
      } finally {
        setIsMarkingFulfillment(false);
      }
    },
    [requestId, hmacToken, successNotification, errorNotification, validateToken]
  );

  const uploadProps = {
    handleUploadFiles,
    loadingFiles,
    progress,
  };

  useEffect(() => {
    const tmpSortedDocuments = [...documentsToShow].sort((a, b) => a.name.localeCompare(b.name));
    setSortedDocuments(tmpSortedDocuments);
  }, [documentsToShow]);

  return (
    <StyledPerfectScrollbar options={{ suppressScrollX: true, wheelSpeed: 3 }}>
      {sortedDocuments.map(requestedFile => {
        if (requestedFile.id === undefined) {
          return null;
        }
        const hasFiles = !isEmpty(requestedFile.files);
        const isNaFulfilled =
          requestedFile.fulfillment_status === 'not_applicable'
          || requestedFile.fulfillment_status === 'not_available';
        // N/A slots stay in EmptyFileBox (shows status); uploaded files use accordion.
        if (hasFiles && !isNaFulfilled) {
          return <FileAccordion {...uploadProps} key={requestedFile.id} requestedFile={requestedFile} />;
        }
        return (
          <EmptyFileBox
            {...uploadProps}
            key={requestedFile.id}
            requestedFile={requestedFile}
            onMarkFulfillment={handleMarkFulfillment}
            isMarkingFulfillment={isMarkingFulfillment}
          />
        );
      })}
    </StyledPerfectScrollbar>
  );
};

export default ProcessManagementDocumentList;
